import type { QueryClient } from '@tanstack/react-query';
import { currentOrganizationQueryOptions } from './queries/organization';
import { portfolioSummaryQueryOptions } from '~/features/portfolio/use-portfolio';

/** The two surfaces a freshly-landed, onboarded user can be routed to. */
export type LandingRoute = '/modeling' | '/uploads';

/**
 * Resolves where to land an onboarded user. Modeling is the default home, but an
 * org with no loans yet has nothing to model — those users land on the uploads
 * page so their first action is to bring data in.
 *
 * Defensive by design: this runs on hot redirect paths (sign-in, org switch,
 * onboarding) where the active org may not be resolved yet. A missing org or any
 * failure degrades to '/modeling' — the `_protected` gate then forwards a
 * non-onboarded user to onboarding, and the Modeling screen's own empty state
 * still offers an upload CTA, so no user is ever stranded.
 *
 * Callers that have just CHANGED the active org (switch / create) must invalidate
 * the `['portfolio']` cache first so the loan count is read fresh for the new
 * tenant (the summary query key is not org-scoped). `useSwitchOrganization`
 * already does this; the create flows invalidate explicitly.
 */
export async function resolveLandingRoute(queryClient: QueryClient): Promise<LandingRoute> {
  try {
    const { organization } = await queryClient.ensureQueryData(currentOrganizationQueryOptions());
    if (!organization) return '/modeling';
    const data = await queryClient.fetchQuery(portfolioSummaryQueryOptions());
    return (data?.summary.totalLoans ?? 0) > 0 ? '/modeling' : '/uploads';
  } catch {
    return '/modeling';
  }
}
